import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDataProvider, useNavigate } from '@work4all/data';

import { Appointment } from '@work4all/models/lib/Classes/Appointment.entity';
import { User } from '@work4all/models/lib/Classes/User.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Filter } from '@work4all/models/lib/DataProvider/filters';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import {
  stringifyTableSettings,
  TableSettings,
} from '../../../../../../components/data-tables/make-table-settings';

export type UseBillableAppointmentsProps = {
  daysBack?: number;
  user: User;
};

export const useBillableAppointments = (
  props: UseBillableAppointmentsProps
) => {
  const { daysBack = 60, user } = props;
  const today = DateTime.now();
  const startDate = today.minus({ days: daysBack }).startOf('day').toISO();

  const request = useMemo<DataRequest>(() => {
    const filters: Filter[] = [
      { startDate: { $gte: startDate } },
      { userId: { $eq: user.id } },
      { parentId: { $eq: 0 } },
      {
        'details.billableStatus': {
          $eq: -1,
        },
      },
    ];

    const dataRequest: DataRequest = {
      entity: Entities.appointment,
      data: {
        id: null,
      } as Appointment<EMode.query>,
      filter: filters,
    };

    return dataRequest;
  }, [user?.id, startDate]);

  const { t } = useTranslation();
  const data = useDataProvider<Appointment>(request);

  const navigate = useNavigate();
  const onClick = useCallback(() => {
    const userFilter: TableSettings = {
      filter: [
        {
          id: 'user.id',
          value: [
            {
              id: user.id.toString(),
              displayName: user?.displayName,
            },
          ],
        },
        {
          id: 'details.billableStatus',
          value: [
            {
              id: true,
              name: t('APPOINTMENT.BILLABLE'),
            },
          ],
        },
        {
          id: 'startDate',
          value: {
            startDate,
            endDate: null,
          },
        },
      ],
    };

    const settings = stringifyTableSettings(userFilter);

    navigate(`/more/entity/appointment?settings=${settings}`);
  }, [startDate, user.id, user?.displayName, t, navigate]);

  return {
    data,
    onClick,
  };
};
